import { type FC, useCallback } from 'react';
import JoinInButton from '../../../shared/JoinIn/JoinInButton';
import PrizeFundCampaign from '../../../shared/PrizeFundCampaign/PrizeFundCampaign';
import StartsIn from '../../../shared/StartsIn/StartsIn';
import PeriodDisplay from '../../../shared/PeriodDisplay/PeriodDisplay';
import { setViewMode, setSelectedSection } from 'store/app/appStore';
import { SECTION_NAMES } from '@repo/common';
import { SINGLE_VIEW_MODE } from '../../../../../../constants';

import { useCampaignDataAttributes } from 'features/campaigns/_exports';
import { useCampaignCardScale } from '../../../../hooks/styling/useCampaignCardScale';
import { usePanelInfo, useIsSingleItem, useAutoJoin, useSelectedCampaignId } from '../../../../store/campaignsStore';
import { useCampaignStatus } from '../../../../hooks/data/campaignStatus/useCampaignStatus';
import { PRIZE_POP_CAMPAIGN_TYPE } from '../../../../types/campaign.types';

import sharedStyles from '../../../shared/CampaignPanel/panel.module.css';

const PrizePopPanel: FC = () => {
  const panelInfo = usePanelInfo(PRIZE_POP_CAMPAIGN_TYPE);
  const isSingleItem = useIsSingleItem(PRIZE_POP_CAMPAIGN_TYPE);
  const { backgroundOverlayImage, headerTitle, prizeFund, prizeCurrency, joined, startDate = '', endDate = '' } = panelInfo;
  const dataAttributes = useCampaignDataAttributes({
    campaignType: PRIZE_POP_CAMPAIGN_TYPE,
    additionalProps: {'data-variant':  'panel'}
  });

  // We need this in case of a single campaign
  const selectedPrizePopId = useSelectedCampaignId(PRIZE_POP_CAMPAIGN_TYPE) as string;

  const autoJoin = useAutoJoin(PRIZE_POP_CAMPAIGN_TYPE);
  const { isFinished } = useCampaignStatus(selectedPrizePopId);
  const showJoinButton = !!selectedPrizePopId && !autoJoin && isSingleItem && (!isFinished || joined);

  const { titleRef, scale } = useCampaignCardScale(headerTitle, {
    recalculateKey: isSingleItem,
  });

  const handleClick = useCallback(() => {
    setSelectedSection(SECTION_NAMES.PRIZE_POP);
    setViewMode(SINGLE_VIEW_MODE);
  }, []);

  return (
    <div
      className={sharedStyles.panelContainer}
      style={backgroundOverlayImage ? { backgroundImage: `url(${backgroundOverlayImage})` } : undefined}
      {...dataAttributes}
    >
      {showJoinButton && <JoinInButton campaignId={selectedPrizePopId} variant="panel" />}
      <div className={sharedStyles.centerInfoWrapper} {...dataAttributes}>
        <span 
          className={sharedStyles.campaignInfoTitle} 
          ref={titleRef as React.RefObject<HTMLSpanElement>}
          style={{
            transform: scale < 1 ? `scale(${scale})` : undefined,
          }}
        >
          {headerTitle}
        </span>
        <PrizeFundCampaign
          amount={prizeFund || 0}
          currency={prizeCurrency ?? ''}
          variant="panel"
        />
      </div>
 
      <div 
        className={sharedStyles.periodWrapper} 
        onClick={handleClick} 
        {...dataAttributes}
      >
        {isSingleItem && !isFinished
          ? <StartsIn />
          : <>
            <div className={sharedStyles.separator} />
            <PeriodDisplay startDate={startDate} endDate={endDate} isFinished={isFinished && isSingleItem}/>
          </>
        }
      </div>
    </div>
  );
};

export default PrizePopPanel;
